import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Building2,
  Calendar,
  CircleHelp,
  ClipboardList,
  FileStack,
  LayoutDashboard,
  Palette,
  Plus,
  ScrollText,
  Settings,
  Shield,
  UserPlus,
  UserRound,
  Users,
} from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/auth/useAuth";
import { usePermissions } from "@/hooks/usePermissions";

const ROUTES = [
  {
    group: "Navigate",
    to: "/platform",
    label: "Platform Dashboard",
    icon: LayoutDashboard,
    keywords: "home overview",
    superuserOnly: true,
  },
  {
    group: "Navigate",
    to: "/",
    label: "Organization Dashboard",
    icon: LayoutDashboard,
    keywords: "home overview",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/schedule",
    label: "Schedule",
    icon: Calendar,
    permission: "scheduling.visits.view",
    keywords: "visits shifts week",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/care-plans",
    label: "Care Plans",
    icon: ClipboardList,
    permission: "careplans.plans.view",
    keywords: "plans clients",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/care-plans/templates",
    label: "Care Plan Templates",
    icon: FileStack,
    permission: "careplans.plans.view",
    keywords: "templates library",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/clients",
    label: "Clients",
    icon: UserRound,
    permission: "directory.clients.view",
    keywords: "directory patients",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/caregivers",
    label: "Caregivers",
    icon: Users,
    permission: "directory.caregivers.view",
    keywords: "staff roster",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/audit-logs",
    label: "Audit Logs",
    icon: ScrollText,
    permission: "audit.logs.view",
    keywords: "security history compliance",
  },
  {
    group: "Navigate",
    to: "/account",
    label: "Account",
    icon: UserRound,
    keywords: "profile password settings",
  },
  {
    group: "Navigate",
    to: "/settings/appearance",
    label: "Appearance",
    icon: Palette,
    keywords: "theme font color customization",
  },
  {
    group: "Navigate",
    to: "/settings/users",
    label: "Team Members",
    icon: Users,
    permission: "organization.users.view",
    keywords: "staff invite",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/settings/org",
    label: "Organization Settings",
    icon: Settings,
    permission: "organization.settings.view",
    keywords: "agency timezone contact",
    agencyOnly: true,
  },
  {
    group: "Navigate",
    to: "/admin/agencies",
    label: "Organizations",
    icon: Building2,
    superuserOnly: true,
    keywords: "tenants agencies admin",
  },
  {
    group: "Navigate",
    to: "/admin/users",
    label: "Users",
    icon: Users,
    superuserOnly: true,
    keywords: "staff provision admin",
  },
  {
    group: "Navigate",
    to: "/admin/roles",
    label: "Roles & Permissions",
    icon: Shield,
    superuserOnly: true,
    keywords: "rbac admin matrix",
  },
  {
    group: "Create",
    to: "/clients/new",
    label: "New Client",
    icon: UserPlus,
    permission: "directory.clients.create",
    keywords: "add create patient",
    agencyOnly: true,
  },
  {
    group: "Create",
    to: "/caregivers/new",
    label: "New Caregiver",
    icon: Plus,
    permission: "directory.caregivers.create",
    keywords: "add create staff",
    agencyOnly: true,
  },
  {
    group: "Create",
    to: "/care-plans/templates/new",
    label: "New Care Plan Template",
    icon: Plus,
    permission: "careplans.plans.create",
    keywords: "template create",
    agencyOnly: true,
  },
  {
    group: "Create",
    to: "/admin/agencies/new",
    label: "Register Organization",
    icon: Building2,
    superuserOnly: true,
    keywords: "create tenant agency",
  },
  {
    group: "Create",
    to: "/admin/users/new",
    label: "Provision Staff User",
    icon: UserPlus,
    superuserOnly: true,
    keywords: "create user",
  },
  {
    group: "Help",
    to: "/help",
    label: "Get Help",
    icon: CircleHelp,
    keywords: "support faq contact",
  },
  {
    group: "Help",
    to: "/docs",
    label: "Docs",
    icon: BookOpen,
    keywords: "documentation guides",
  },
  {
    group: "Help",
    to: "/docs/getting-started",
    label: "Docs: Getting started",
    icon: BookOpen,
    keywords: "documentation onboarding permissions timezone",
  },
  {
    group: "Help",
    to: "/docs/schedule-visits",
    label: "Docs: Schedule & visits",
    icon: Calendar,
    permission: "scheduling.visits.view",
    agencyOnly: true,
    keywords: "documentation schedule visit check-in",
  },
  {
    group: "Help",
    to: "/docs/clients",
    label: "Docs: Clients",
    icon: UserRound,
    permission: "directory.clients.view",
    agencyOnly: true,
    keywords: "documentation clients directory discharge",
  },
  {
    group: "Help",
    to: "/docs/appearance",
    label: "Docs: Appearance",
    icon: Palette,
    keywords: "documentation theme font dark mode",
  },
  {
    group: "Help",
    to: "/docs/roles-permissions",
    label: "Docs: Roles & permissions",
    icon: Shield,
    superuserOnly: true,
    keywords: "documentation rbac matrix roles",
  },
  {
    group: "Help",
    to: "/docs/platform-users",
    label: "Docs: Platform users",
    icon: Users,
    superuserOnly: true,
    keywords: "documentation provision users superuser",
  },
];

const GROUP_ORDER = ["Navigate", "Create", "Help"];

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();

  const items = useMemo(() => {
    return ROUTES.filter((item) => {
      if (item.superuserOnly && !user?.is_superuser) return false;
      if (item.agencyOnly && user?.is_superuser) return false;
      if (item.permission && !can(item.permission)) return false;
      return true;
    });
  }, [user, can]);

  const groups = useMemo(() => {
    return GROUP_ORDER.map((name) => ({
      name,
      items: items.filter((item) => item.group === name),
    })).filter((group) => group.items.length > 0);
  }, [items]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Jump to a page…" />
      <CommandList>
        <CommandEmpty>No matching pages. Try “schedule”, “clients”, or “account”.</CommandEmpty>
        {groups.map((group, index) => (
          <div key={group.name}>
            {index > 0 ? <CommandSeparator /> : null}
            <CommandGroup heading={group.name}>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.to}
                    value={`${item.label} ${item.keywords || ""}`}
                    onSelect={() => {
                      onOpenChange(false);
                      navigate(item.to);
                    }}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span>{item.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
